import React from 'react';
import { useLanguage } from '@/contexts/LanguageContext';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { ChevronRight, TreePine, Mountain, Leaf, Shovel, HardHat } from 'lucide-react';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import MobileCTA from '@/components/MobileCTA';
import SEO from '@/components/SEO';
import MembershipDisclaimerForm from '@/components/MembershipDisclaimerForm';
import AIReadabilityBlock from '@/components/AIReadabilityBlock';
import ExpandedFAQSection from '@/components/ExpandedFAQSection';
import RelatedMaterialsLinks from '@/components/RelatedMaterialsLinks';
import { productsFaqs } from '@/data/productFaqs';

const ProductsPage = () => {
  const { language, t } = useLanguage();

  const seoTitle = language === 'en' 
    ? 'Premium Landscaping Materials Texas | Grass, Soil, Rock, Mulch'
    : 'Materiales de Paisajismo Premium Texas | Pasto, Tierra, Roca, Mulch';
  
  const seoDescription = language === 'en'
    ? 'Quality landscaping materials in Montgomery County TX. Sod, topsoil, decorative rock, organic mulch, sand. Professional delivery service available.'
    : 'Materiales de paisajismo de calidad en el Condado Montgomery TX. Césped, tierra superior, roca decorativa, mulch orgánico, arena. Servicio de entrega profesional disponible.';
  
  const seoKeywords = language === 'en'
    ? 'landscaping materials texas, sod grass soil rock mulch montgomery county, landscaping supplies conroe woodlands cypress'
    : 'materiales paisajismo texas, césped tierra roca mulch montgomery county, suministros paisajismo conroe woodlands cypress';

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "ItemList",
    "name": seoTitle,
    "description": seoDescription,
    "numberOfItems": 5,
    "itemListElement": [
      {
        "@type": "Product",
        "position": 1,
        "name": language === 'en' ? "Premium Grass & Sod" : "Pasto y Césped Premium",
        "url": `/${language}/${language === 'en' ? 'grass' : 'pasto'}`
      },
      {
        "@type": "Product", 
        "position": 2,
        "name": language === 'en' ? "Premium Soil & Topsoil" : "Tierra Premium y Superior",
        "url": `/${language}/${language === 'en' ? 'soil' : 'tierra'}`
      },
      {
        "@type": "Product",
        "position": 3,
        "name": language === 'en' ? "Decorative Rock & Stone" : "Roca Decorativa y Piedra",
        "url": `/${language}/${language === 'en' ? 'rock' : 'roca'}`
      },
      {
        "@type": "Product",
        "position": 4,
        "name": language === 'en' ? "Organic Mulch" : "Mulch Orgánico",
        "url": `/${language}/mulch`
      },
      {
        "@type": "Product",
        "position": 5,
        "name": language === 'en' ? "Premium Sand" : "Arena Premium",
        "url": `/${language}/${language === 'en' ? 'sand' : 'arena'}`
      }
    ]
  };

  const productCategories = [
    {
      key: 'grass',
      title: t('nav.grass'),
      description: language === 'en' 
        ? 'Premium sod varieties for every landscape need' 
        : 'Variedades de césped premium para cada necesidad paisajística',
      icon: TreePine,
      href: `/${language}/${language === 'en' ? 'grass' : 'pasto'}`,
      features: language === 'en' 
        ? ['St. Augustine varieties', 'Bermuda grass', 'Zoysia options', 'Bahia & Carpet grass']
        : ['Variedades St. Augustine', 'Césped Bermuda', 'Opciones Zoysia', 'Bahia y Carpet grass']
    },
    {
      key: 'rock',
      title: t('nav.rock'),
      description: language === 'en'
        ? 'Decorative and functional rock materials'
        : 'Materiales de roca decorativos y funcionales',
      icon: Mountain,
      href: `/${language}/${language === 'en' ? 'rock' : 'roca'}`,
      features: language === 'en'
        ? ['Bull Rock', 'River Rock', 'Pea Gravel', 'Limestone & Granite']
        : ['Bull Rock', 'River Rock', 'Grava de guisante', 'Piedra caliza y granito']
    },
    {
      key: 'mulch',
      title: t('nav.mulch'),
      description: language === 'en'
        ? 'Natural mulch for soil health and aesthetics'
        : 'Mantillo natural para la salud del suelo y estética',
      icon: Leaf,
      href: `/${language}/mulch`,
      features: language === 'en'
        ? ['Black Mulch', 'Brown Mulch', 'Red Mulch', 'Hardwood varieties']
        : ['Mantillo negro', 'Mantillo marrón', 'Mantillo rojo', 'Variedades de madera dura']
    },
    {
      key: 'soil',
      title: t('nav.soil'),
      description: language === 'en'
        ? 'Quality soil mixtures for optimal plant growth'
        : 'Mezclas de suelo de calidad para crecimiento óptimo',
      icon: Shovel,
      href: `/${language}/${language === 'en' ? 'soil' : 'tierra'}`,
      features: language === 'en'
        ? ['Top Soil', 'Garden Soil', 'Compost', 'Custom blends']
        : ['Tierra vegetal', 'Tierra de jardín', 'Compost', 'Mezclas personalizadas']
    },
    {
      key: 'sand',
      title: t('nav.sand'),
      description: language === 'en'
        ? 'Various sand types for construction and landscaping'
        : 'Varios tipos de arena para construcción y paisajismo',
      icon: HardHat,
      href: `/${language}/${language === 'en' ? 'sand' : 'arena'}`,
      features: language === 'en'
        ? ['Mason Sand', 'Play Sand', 'Concrete Sand', 'Fill sand']
        : ['Arena de albañil', 'Arena de juego', 'Arena de concreto', 'Arena de relleno']
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-subtle">
      <SEO 
        title={seoTitle}
        description={seoDescription}
        keywords={seoKeywords}
        canonical={language === 'en' ? '/en/products' : '/es/productos'}
        structuredData={structuredData}
        language={language}
        breadcrumbs={[
          { name: language === 'en' ? 'Home' : 'Inicio', url: `/${language}` },
          { name: language === 'en' ? 'Products' : 'Productos', url: language === 'en' ? '/en/products' : '/es/productos' },
        ]}
      />
      <Header />
      <MobileCTA />
      
      <main>
      {/* Hero Section */}
      <section className="relative py-20 bg-gradient-hero text-white overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-primary/20 to-transparent animate-pulse-glow"></div>
        <div className="container mx-auto px-6 text-center relative z-10">
          <div className="animate-fade-in">
            <h1 className="text-4xl md:text-6xl font-bold mb-6 animate-scale-in">
              {language === 'en' ? 'Our Products' : 'Nuestros Productos'}
            </h1>
            <p className="text-xl md:text-2xl text-white/90 max-w-3xl mx-auto mb-8 animate-fade-in-delayed">
              {language === 'en' 
                ? 'Premium landscaping materials for every project, from residential gardens to commercial developments'
                : 'Materiales de paisajismo premium para cada proyecto, desde jardines residenciales hasta desarrollos comerciales'
              }
            </p>
            <div className="flex flex-wrap justify-center gap-4 animate-slide-up">
              <div className="bg-white/10 backdrop-blur-sm rounded-full px-6 py-3 border border-white/20">
                <span className="text-white font-medium">
                  {language === 'en' ? '🌱 100% Natural Materials' : '🌱 100% Materiales Naturales'}
                </span>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-full px-6 py-3 border border-white/20">
                <span className="text-white font-medium">
                  {language === 'en' ? '🚚 Fast Delivery' : '🚚 Entrega Rápida'}
                </span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* AI Readability Block - Optimized for LLM extraction */}
      <AIReadabilityBlock variant="products" />

      {/* Products Grid */}
      <section className="py-20">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16 animate-fade-in">
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
              {language === 'en' ? 'Premium Materials Collection' : 'Colección de Materiales Premium'}
            </h2>
            <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
              {language === 'en'
                ? 'Discover our comprehensive range of high-quality landscaping materials, carefully selected for Texas climates'
                : 'Descubre nuestra gama completa de materiales de paisajismo de alta calidad, cuidadosamente seleccionados para climas de Texas'
              }
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {productCategories.map((category, index) => {
              const IconComponent = category.icon;
              return (
                <Card 
                  key={category.key} 
                  className="group hover:shadow-glow transition-all duration-500 border-2 hover:border-primary relative overflow-hidden animate-fade-in"
                  style={{ animationDelay: `${index * 100}ms` }}
                >
                  <div className="absolute inset-0 bg-gradient-to-br from-primary/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                  <CardHeader className="text-center pb-4 relative z-10">
                    <div className="mx-auto w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mb-4 group-hover:bg-primary/20 transition-all duration-300 group-hover:scale-110">
                      <IconComponent className="h-8 w-8 text-primary group-hover:animate-float" />
                    </div>
                    <CardTitle className="text-2xl font-bold text-foreground group-hover:text-primary transition-colors duration-300">
                      {category.title}
                    </CardTitle>
                    <CardDescription className="text-base text-muted-foreground group-hover:text-foreground transition-colors">
                      {category.description}
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="relative z-10">
                    <ul className="space-y-2 mb-6">
                      {category.features.map((feature, index) => (
                        <li key={index} className="flex items-center text-sm text-muted-foreground group-hover:text-foreground transition-colors">
                          <ChevronRight className="h-4 w-4 text-primary mr-2 flex-shrink-0 group-hover:translate-x-1 transition-transform" />
                          {feature}
                        </li>
                      ))}
                    </ul>
                    <Link to={category.href}>
                      <Button variant="outline" className="w-full group-hover:bg-primary group-hover:text-primary-foreground transition-all duration-300 hover-scale">
                        {language === 'en' ? 'View Products' : 'Ver Productos'}
                        <ChevronRight className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform" />
                      </Button>
                    </Link>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Membership Section */}
      <section className="py-16 bg-background">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto">
            <MembershipDisclaimerForm showInline />
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-secondary text-secondary-foreground">
        <div className="container mx-auto px-6 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            {language === 'en' 
              ? 'Need Help Choosing the Right Products?' 
              : '¿Necesitas ayuda eligiendo los productos correctos?'
            }
          </h2>
          <p className="text-lg text-secondary-foreground/80 mb-8 max-w-2xl mx-auto">
            {language === 'en'
              ? 'Our experts are here to help you select the perfect materials for your landscaping project.'
              : 'Nuestros expertos están aquí para ayudarte a seleccionar los materiales perfectos para tu proyecto de paisajismo.'
            }
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to={`/${language}/${language === 'en' ? 'quote' : 'cotizar'}`}>
              <Button variant="hero" size="lg">
                {language === 'en' ? 'Get Free Quote' : 'Obtener Cotización Gratis'}
              </Button>
            </Link>
            <a href="tel:9363200763">
              <Button variant="outline" size="lg" className="border-secondary-foreground/20 text-green-700 hover:bg-secondary-foreground/10">
                {language === 'en' ? 'Call Now' : 'Llamar Ahora'}
              </Button>
            </a>
          </div>
        </div>
      </section>
      {/* Materials comparison table */}
      <section className="py-16 bg-background" aria-labelledby="materials-compare-heading">
        <div className="container mx-auto px-4">
          <header className="text-center mb-10 max-w-3xl mx-auto">
            <h2 id="materials-compare-heading" className="text-3xl md:text-4xl font-bold text-foreground mb-4">
              {language === 'en' ? 'Compare Our Landscaping Materials' : 'Compara Nuestros Materiales de Paisajismo'}
            </h2>
            <p className="text-lg text-muted-foreground">
              {language === 'en'
                ? 'Quick reference to pick the right material for your Texas project.'
                : 'Referencia rápida para elegir el material correcto para tu proyecto en Texas.'}
            </p>
          </header>
          <div className="max-w-5xl mx-auto overflow-x-auto">
            <table className="w-full text-sm border rounded-lg overflow-hidden bg-card">
              <thead className="bg-muted text-foreground">
                <tr>
                  <th className="text-left p-3">{language === 'en' ? 'Material' : 'Material'}</th>
                  <th className="text-left p-3">{language === 'en' ? 'Best For' : 'Mejor Para'}</th>
                  <th className="text-left p-3">{language === 'en' ? 'Sold By' : 'Se Vende Por'}</th>
                  <th className="text-left p-3">{language === 'en' ? 'Typical Depth' : 'Profundidad Típica'}</th>
                  <th className="text-left p-3">{language === 'en' ? 'Explore' : 'Explorar'}</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                <tr>
                  <td className="p-3 font-medium">{language === 'en' ? 'Grass & Sod' : 'Pasto y Césped'}</td>
                  <td className="p-3 text-muted-foreground">{language === 'en' ? 'New lawns, resod, athletic areas' : 'Nuevos céspedes, re-cesped, áreas deportivas'}</td>
                  <td className="p-3 text-muted-foreground">{language === 'en' ? 'Pallet (~450 sq ft)' : 'Pallet (~450 pies²)'}</td>
                  <td className="p-3 text-muted-foreground">—</td>
                  <td className="p-3"><Link to={`/${language}/${language === 'en' ? 'grass' : 'pasto'}`} className="text-primary hover:underline">{language === 'en' ? 'View grass' : 'Ver pasto'}</Link></td>
                </tr>
                <tr>
                  <td className="p-3 font-medium">{language === 'en' ? 'Topsoil & Garden Soil' : 'Tierra y Tierra de Jardín'}</td>
                  <td className="p-3 text-muted-foreground">{language === 'en' ? 'Planting beds, grading, raised beds' : 'Camas de plantas, nivelación, camas elevadas'}</td>
                  <td className="p-3 text-muted-foreground">{language === 'en' ? 'Cubic yard' : 'Yarda cúbica'}</td>
                  <td className="p-3 text-muted-foreground">{language === 'en' ? '4–12 in' : '10–30 cm'}</td>
                  <td className="p-3"><Link to={`/${language}/${language === 'en' ? 'soil' : 'tierra'}`} className="text-primary hover:underline">{language === 'en' ? 'View soil' : 'Ver tierra'}</Link></td>
                </tr>
                <tr>
                  <td className="p-3 font-medium">{language === 'en' ? 'Decorative Rock' : 'Roca Decorativa'}</td>
                  <td className="p-3 text-muted-foreground">{language === 'en' ? 'Drainage, paths, dry creeks' : 'Drenaje, caminos, arroyos secos'}</td>
                  <td className="p-3 text-muted-foreground">{language === 'en' ? 'Cubic yard' : 'Yarda cúbica'}</td>
                  <td className="p-3 text-muted-foreground">{language === 'en' ? '2–4 in' : '5–10 cm'}</td>
                  <td className="p-3"><Link to={`/${language}/${language === 'en' ? 'rock' : 'roca'}`} className="text-primary hover:underline">{language === 'en' ? 'View rock' : 'Ver roca'}</Link></td>
                </tr>
                <tr>
                  <td className="p-3 font-medium">{language === 'en' ? 'Organic Mulch' : 'Mulch Orgánico'}</td>
                  <td className="p-3 text-muted-foreground">{language === 'en' ? 'Moisture retention, weed control, curb appeal' : 'Retención de humedad, control de maleza, estética'}</td>
                  <td className="p-3 text-muted-foreground">{language === 'en' ? 'Cubic yard' : 'Yarda cúbica'}</td>
                  <td className="p-3 text-muted-foreground">{language === 'en' ? '2–3 in' : '5–8 cm'}</td>
                  <td className="p-3"><Link to={`/${language}/mulch`} className="text-primary hover:underline">{language === 'en' ? 'View mulch' : 'Ver mulch'}</Link></td>
                </tr>
                <tr>
                  <td className="p-3 font-medium">{language === 'en' ? 'Sand' : 'Arena'}</td>
                  <td className="p-3 text-muted-foreground">{language === 'en' ? 'Leveling, paver base, play areas, concrete' : 'Nivelación, base de adoquines, juego, concreto'}</td>
                  <td className="p-3 text-muted-foreground">{language === 'en' ? 'Cubic yard' : 'Yarda cúbica'}</td>
                  <td className="p-3 text-muted-foreground">{language === 'en' ? '1/4–2 in' : '0.5–5 cm'}</td>
                  <td className="p-3"><Link to={`/${language}/${language === 'en' ? 'sand' : 'arena'}`} className="text-primary hover:underline">{language === 'en' ? 'View sand' : 'Ver arena'}</Link></td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </section>

      <ExpandedFAQSection faqs={productsFaqs} language={language as 'en' | 'es'} />

      <RelatedMaterialsLinks language={language as 'en' | 'es'} exclude={['products']} />
      </main>
      
      <Footer />
    </div>
  );
};

export default ProductsPage;